import { useTranslation } from "@client-app/i18n";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  billingService,
  CreditTopupEligibilityResponse,
  CreditTopupSku,
} from "@common/api/billingService";
import { CaretDown, Check, CircleNotch } from "@phosphor-icons/react";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import {
  buildCreditTopupReturnUrls,
  writePendingCreditTopupCheckoutSessionId,
} from "./creditTopupReturnParams";

interface BuyMoreCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId?: string | null;
  skuOptions: CreditTopupSku[];
  eligibility: CreditTopupEligibilityResponse | null;
  isTopupDataLoading: boolean;
  topupDataError: string | null;
  onRetryTopupData: () => void;
}

const topupEligibilityMessage = (
  reasonCode: string | null | undefined,
  t: ReturnType<typeof useTranslation>["t"],
) => {
  switch (reasonCode) {
    case "missing_topup_permission":
      return t("website.topup.permissionDeniedBuyCredits");
    case "not_workspace_member":
      return t("website.topup.joinWorkspaceToBuyCredits");
    case "free_plan":
    case "trial_plan":
    case "unsupported_plan":
      return t("website.topup.availableForSoloAndTeam");
    case "plan_expired":
    case "canceled_plan":
    case "expired_plan":
    case "past_due_plan":
      return t("website.topup.restorePaidPlan");
    default:
      return t("website.topup.availableForActivePaidPlans");
  }
};

const getSkuWorkUnitsGranted = (option: CreditTopupSku | undefined) =>
  option?.workUnitsGranted ?? option?.creditsGranted ?? 0;

const BuyMoreCreditsModal: React.FC<BuyMoreCreditsModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  skuOptions,
  eligibility,
  isTopupDataLoading,
  topupDataError,
  onRetryTopupData,
}) => {
  const { locale, t } = useTranslation();
  const [selectedSkuCode, setSelectedSkuCode] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!isOpen || isTopupDataLoading) return;
    if (skuOptions.length > 0 && eligibility) return;
    onRetryTopupData();
  }, [
    eligibility,
    isOpen,
    isTopupDataLoading,
    onRetryTopupData,
    skuOptions.length,
  ]);

  useEffect(() => {
    setSelectedSkuCode((current) => {
      if (skuOptions.some((option) => option.skuCode === current)) {
        return current;
      }
      return skuOptions[0]?.skuCode || "";
    });
  }, [skuOptions]);

  useEffect(() => {
    if (!isOpen) {
      setDropdownOpen(false);
      setIsLoading(false);
      setCheckoutErrorMessage(null);
    }
  }, [isOpen]);

  const isEligible = eligibility?.eligible !== false;
  const eligibilityErrorMessage =
    eligibility && !eligibility.eligible
      ? topupEligibilityMessage(eligibility.reasonCode, t)
      : null;
  const errorMessage =
    checkoutErrorMessage || topupDataError || eligibilityErrorMessage;

  const handleNext = async () => {
    if (!isEligible) {
      return;
    }
    setIsLoading(true);
    setCheckoutErrorMessage(null);
    try {
      const { successUrl, cancelUrl } = buildCreditTopupReturnUrls({
        workspaceId,
      });
      const checkout = await billingService.createCreditTopupCheckout({
        skuCode: selectedSkuCode,
        idempotencyKey:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${selectedSkuCode}`,
        successUrl,
        cancelUrl,
      });
      const checkoutUrl = checkout.checkoutUrl || checkout.paymentUrl;
      if (checkoutUrl) {
        writePendingCreditTopupCheckoutSessionId(checkout.sessionId);
        window.location.assign(checkoutUrl);
        return;
      }
      setCheckoutErrorMessage(t("website.topup.checkoutUrlMissing"));
      setIsLoading(false);
    } catch (error: any) {
      setCheckoutErrorMessage(error?.message || t("website.topup.failedToStartCheckout"));
      setIsLoading(false);
    }
  };

  const selectedOption = useMemo(
    () =>
      skuOptions.find((option) => option.skuCode === selectedSkuCode) ||
      skuOptions[0],
    [selectedSkuCode, skuOptions],
  );
  const handlePresetSelect = (skuCode: string) => {
    setSelectedSkuCode(skuCode);
    setDropdownOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? () => undefined : onClose}
      title={t("website.topup.title")}
      width="max-w-[560px]"
      hideCloseButton={isLoading}
      containerClassName="overflow-visible"
      bodyClassName="px-6 pb-6 pt-0"
      noPadding
    >
      <div className="mb-6 text-sm leading-6 text-[#666F8D]">
        {t("website.topup.description")}
      </div>

      <div className="mb-8">
        <label className="mb-3 block text-sm font-medium text-[#1A162F]">
          {t("website.topup.amountToAdd")}
        </label>
        <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              disabled={
                isLoading || isTopupDataLoading || skuOptions.length === 0
              }
              className={clsx(
                "flex w-full items-center justify-between rounded-[10px] border px-4 py-3 text-left transition-all",
                dropdownOpen
                  ? "border-[#1A162F] bg-[#F8F9FC] ring-2 ring-[#1A162F]/20"
                  : "border-[#E8EBF3] bg-white hover:border-[#C8D0E2]",
                isLoading && "cursor-not-allowed opacity-60",
              )}
            >
              <div className="flex w-full items-center justify-between text-sm">
                <span className="font-medium text-[#1A162F]">
                  {new Intl.NumberFormat(locale).format(
                    getSkuWorkUnitsGranted(selectedOption),
                  )}{" "}
                  {t("website.topup.credits")}
                </span>
                <span className="text-[#666F8D] [font-variant-numeric:tabular-nums]">
                  {new Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format((selectedOption?.amountCents ?? 0) / 100)}
                </span>
              </div>
              <CaretDown
                size={16}
                className={clsx(
                  "ml-2 shrink-0 text-[#8A93B2] transition-transform duration-200",
                  dropdownOpen && "rotate-180",
                )}
              />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
              side="top"
              sideOffset={6}
              align="start"
              collisionPadding={16}
              style={{
                width: "var(--radix-dropdown-menu-trigger-width)",
              }}
            >
              <div className="max-h-[360px] overflow-x-hidden overflow-y-auto p-1.5">
                <div className="space-y-0.5">
                  {skuOptions.map((option) => {
                    const isSelected = selectedSkuCode === option.skuCode;
                    return (
                      <DropdownMenu.Item
                        key={option.skuCode}
                        onSelect={() => handlePresetSelect(option.skuCode)}
                        className={clsx(
                          "flex cursor-pointer items-center justify-between rounded-lg px-3 py-3 text-sm outline-none transition-colors",
                          isSelected ? "bg-[#1A162F]/5" : "hover:bg-gray-50",
                        )}
                      >
                        <span
                          className={clsx(
                            "font-medium",
                            isSelected ? "text-[#1A162F]" : "text-text-main",
                          )}
                        >
                          {new Intl.NumberFormat(locale).format(
                            getSkuWorkUnitsGranted(option),
                          )}{" "}
                          {t("website.topup.credits")}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-[#666F8D] [font-variant-numeric:tabular-nums]">
                            {new Intl.NumberFormat(locale, {
                              style: "currency",
                              currency: "USD",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(option.amountCents / 100)}
                          </span>
                          {isSelected ? (
                            <Check
                              size={14}
                              weight="regular"
                              className="shrink-0 text-[#1A162F]"
                            />
                          ) : (
                            <div className="w-[14px]" />
                          )}
                        </div>
                      </DropdownMenu.Item>
                    );
                  })}
                </div>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        {errorMessage ? (
          <p className="mt-3 text-sm text-red-600">{errorMessage}</p>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-3">
        <button
          onClick={onClose}
          disabled={isLoading}
          className="rounded-[10px] border border-[#E8EBF3] bg-white px-5 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("common.cancel")}
        </button>
        <button
          onClick={handleNext}
          disabled={
            isLoading || isTopupDataLoading || !selectedSkuCode || !isEligible
          }
          className="flex items-center justify-center gap-2 rounded-[10px] bg-[#1A162F] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#231D3E] disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
        >
          {isLoading ? (
            <>
              <CircleNotch size={16} weight="bold" className="animate-spin" />
              <span>{t("common.loading")}</span>
            </>
          ) : (
            t("website.topup.next")
          )}
        </button>
      </div>
    </Modal>
  );
};

export default BuyMoreCreditsModal;
